import { LevelStatus } from './LevelStatus';
import {
    CardValue,
    getCardSuit,
    getCardValue,
    Suit,
} from 'Utility/CardsHelper';
import { Quests, QuestType } from '../Player/QuestType';
import UIStateHelper from '../UI/UIStateHelper';
import LevelHelper, { getEpisodeIndex } from '../../Utility/LevelHelper';
import { ArrayHelper } from 'Utility/ArrayHelper';
import {
    levelCompleteRewardMultiplier,
    multiplierPerBoosterType,
    remainingStackRewardStartFactor,
    remainingStackRewardStartLinearFactor,
} from 'Constants/Economy';
import { AnyBoosterType, BoosterType, createAnyBoostersRecord } from '../Card/BoosterType';
import CardStateHelper from '../Card/CardStateHelper';
import { FBPixel } from 'Utility/SDK/FBPixelHandler';
import Game from 'Game';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import { WinCriteriaType } from 'Constants/WinCriteria';
import { LevelHelperSettings, lastFreePlusFiveBooster, STREAK_PROGRESS_STEPS } from 'Constants/Level';
import { LevelType } from '../Player/Progress/PlayerLevels';
import { Immutable, unexpectedValue } from 'Utility/Types';
import { ILevelState, ILevelStateTaskData } from './ILevelState';
import { IGameState } from '../Game/IGameState';
import { ICardsState, ICardStateData } from '../Card/ICardState';
import { INTERSTITIAL_FREQUENCY, INTERSTITIAL_START_LEVEL } from 'Constants/Ads';
import { CardType } from 'Prototype/Level/Card/CardType';
import { QuestStateHelper } from '../Player/Quests/QuestStateHelper';
import { ProgressStateHelper } from '../Player/Progress/ProgressStateHelper';
import { FTUX_LEVEL_TASKS } from 'Constants/FTUX';
import { ILevelDataFile } from 'System/Level/Data/ILevelDataFile';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { getFieldUnlockShare } from 'Constants/AvatarsAndCrops';

const SUIT_QUEST_MAPPING: Record<Suit, QuestType> = {
    [Suit.CLUBS]: Quests.CLEAR_CARD_CLUBS,
    [Suit.HEARTS]: Quests.CLEAR_CARD_HEARTS,
    [Suit.SPADES]: Quests.CLEAR_CARD_SPADES,
    [Suit.DIAMONDS]: Quests.CLEAR_CARD_DIAMONDS,
};

const VALUE_QUEST_MAPPING: Record<CardValue, QuestType> = {
    [CardValue.ACE]: Quests.CLEAR_CARD_ACES,
    [CardValue.TWO]: Quests.CLEAR_CARD_2,
    [CardValue.THREE]: Quests.CLEAR_CARD_3,
    [CardValue.FOUR]: Quests.CLEAR_CARD_4,
    [CardValue.FIVE]: Quests.CLEAR_CARD_5,
    [CardValue.SIX]: Quests.CLEAR_CARD_6,
    [CardValue.SEVEN]: Quests.CLEAR_CARD_7,
    [CardValue.EIGHT]: Quests.CLEAR_CARD_8,
    [CardValue.NINE]: Quests.CLEAR_CARD_9,
    [CardValue.TEN]: Quests.CLEAR_CARD_10,
    [CardValue.JACK]: Quests.CLEAR_CARD_JACKS,
    [CardValue.QUEEN]: Quests.CLEAR_CARD_QUEENS,
    [CardValue.KING]: Quests.CLEAR_CARD_KINGS,
};

const LEVELS_TO_TRACK = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 150, 200];

export default class LevelStateHelper {
    public static updateQuestProgress(oldState: IGameState, previousState: IGameState): IGameState {
        let state = { ...oldState };

        // Get "cleared" cards (folded and discarded).
        const clearedCards = [].concat(
            state.levelState.cardsState.folded.filter(e => !previousState.levelState.cardsState.folded.includes(e)),
            state.levelState.cardsState.discarded.filter(
                e => !previousState.levelState.cardsState.discarded.includes(e)
            )
        );

        // Update clear cards quest progress.
        for (let i = 0; i < clearedCards.length; i++) {
            const cardData = state.levelState.cardsState.cards[clearedCards[i]];
            if (
                cardData.type === CardType.value &&
                previousState.levelState.cardsState.inPlay.includes(clearedCards[i])
            ) {
                state = {
                    ...state,
                    playerState: QuestStateHelper.incrementQuestProgressBy(
                        state.playerState,
                        SUIT_QUEST_MAPPING[getCardSuit(cardData.value)],
                        1
                    ),
                };
                state = {
                    ...state,
                    playerState: QuestStateHelper.incrementQuestProgressBy(
                        state.playerState,
                        VALUE_QUEST_MAPPING[getCardValue(cardData.value)],
                        1
                    ),
                };
                state = {
                    ...state,
                    playerState: QuestStateHelper.incrementQuestProgressBy(
                        state.playerState,
                        Quests.CLEAR_CARD_ANY,
                        1
                    ),
                };
            }
        }

        // Update streak quests progress.
        if (
            state.levelState.cardsState.matchSequence.length > 0 &&
            state.levelState.cardsState.matchSequence.length % STREAK_PROGRESS_STEPS === 0
        ) {
            state = {
                ...state,
                playerState: QuestStateHelper.incrementQuestProgressBy(
                    state.playerState,
                    Quests.COMBO_STREAK_BONUS,
                    1
                ),
            };
        }

        return state;
    }

    public static levelComplete(state: IGameState): IGameState {
        // Calculate bonus cards' points. For each card remaining in the stack add 5 points plus another 5
        // points for each card.
        const increment = 5;

        const remainingCards = [
            ...state.levelState.cardsState.inStack,
            ...(isGoldLevel(state.levelState) ? state.levelState.cardsState.inPlay : []),
        ].map(cardID => state.levelState.cardsState.cards[cardID]);

        const bonus = (increment / 2) * remainingCards.length * (remainingCards.length + 1);
        const score = state.levelState.score + bonus;
        const stars = state.levelState.stars.filter(n => n <= score).length;

        const playerLevelData = state.playerState.levels[state.levelState.levelType].find(
            levelData => state.levelState.levelNumber === levelData.levelNumber
        );

        const starsGained = playerLevelData.stars ? stars - playerLevelData.stars : stars;

        const firstComplete: boolean = !playerLevelData.stars && stars > 0;

        const coins =
            !playerLevelData.stars && stars > 0
                ? state.levelState.coins[stars - 1]
                : playerLevelData.stars < stars
                ? state.levelState.coins[stars - 1] - state.levelState.coins[playerLevelData.stars - 1]
                : 0;

        const levelCost: number = LevelHelper.getLevelCost(playerLevelData.levelNumber);

        let stackReward = 0;
        if (firstComplete) {
            stackReward += getAllRemainingCardsReward({
                levelCost,
                startFactor: remainingStackRewardStartFactor,
                linearFactor: remainingStackRewardStartLinearFactor,
                cards: remainingCards,
                firstComplete,
            });
        }

        state = {
            ...state,
            levelState: {
                ...state.levelState,
                score: score,
                bonus: bonus,
                status: LevelStatus.COMPLETE,
                completeData: {
                    levelNumber: state.levelState.levelNumber,
                    score,
                    stars,
                    coins,
                    stackReward,
                    firstComplete,
                    starsGained,
                },
            },
        };

        const levelNumber: number = state.levelState.levelNumber;
        if (firstComplete && (levelNumber === 5 || levelNumber === 10 || levelNumber === 20)) {
            FBPixel.track(`Pixel_SOFA_Completelvl_${levelNumber}`);
        }

        const trackingLevel: number = LEVELS_TO_TRACK.find(level => levelNumber === level);
        if (firstComplete && trackingLevel) {
            const trackString = 'levelComplete_' + String(trackingLevel);
            Game.sdk.gameTracking(Object.values(FBTrackingEvent).find(event => event === trackString));
        }

        //state = LevelStateHelper.checkScoreBeaten(state);
        //state = LevelStateHelper.check3StarReward(state);

        return state;
    }

    public static checkScoreBeaten(state: IGameState): IGameState {
        const entries = state.levelState.leaderboard;

        // Find the player position in the current leaderboard.
        const playerIx = entries.findIndex(entry => entry.user.isCurrentUser);

        // If the player was not the first.
        if (playerIx !== 0) {
            // Look for the first friend just below the current score.
            // If it was the first attempt (he is not in the leaderboard), consider the player as the last.
            const len = playerIx === -1 ? entries.length : playerIx;
            for (let i = 0; i < len - 1; i++) {
                if (state.levelState.score > entries[i].score) {
                    return {
                        ...state,
                        UIState: UIStateHelper.queuePopup(state.UIState, 'scoreBeatenMenu', {
                            levelNumber: state.levelState.levelNumber,
                            playerEntry: entries[playerIx],
                            friendEntry: entries[i],
                        }),
                    };
                }
            }
        }

        return state;
    }

    public static check3StarReward(state: IGameState): IGameState {
        if (state.levelState.firstAttempt && state.levelState.completeData.stars === 3) {
            return {
                ...state,
                UIState: UIStateHelper.queuePopup(state.UIState, 'threeStarRewardMenu', {
                    levelNumber: state.levelState.levelNumber,
                }),
            };
        }
        return state;
    }

    public static setStateTaskData(state: IGameState, taskData: ILevelStateTaskData): IGameState {
        state.levelState.tasks = state.levelState.tasks || [];
        return {
            ...state,
            levelState: {
                ...state.levelState,
                tasks: [...state.levelState.tasks, taskData],
            },
        };
    }

    public static getCoinRewardAmount(state: IGameState): IGameState {
        const levelCost: number = LevelHelper.getLevelCost(state.levelState.levelNumber);

        const coins: number[] = levelCompleteRewardMultiplier.map((multiplier: number) => multiplier * levelCost);
        return {
            ...state,
            levelState: {
                ...state.levelState,
                coins,
            },
        };
    }

    public static getBoosterPrices(state: IGameState): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                boosterPrices: createAnyBoostersRecord(boosterType =>
                    LevelStateHelper.getBoosterPrice(
                        boosterType,
                        state.levelState.levelNumber,
                        state.levelState.boosterUses
                    )
                ),
            },
        };
    }

    public static getBoosterPrice(
        boosterType: AnyBoosterType,
        levelNumber: number,
        boosterUses?: Readonly<ILevelState['boosterUses']>
    ) {
        if (boosterType === BoosterType.PLUS_FIVE && levelNumber <= lastFreePlusFiveBooster) {
            return 0;
        }

        const levelCost = LevelHelper.getLevelCost(levelNumber);
        const multiplier = ArrayHelper.getClamped(
            multiplierPerBoosterType[boosterType],
            boosterUses?.[boosterType] ?? 0
        )!;
        return Math.round(levelCost * multiplier);
    }

    public static ensureBoosterUses(state: IGameState): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                boosterUses: state.levelState.boosterUses ?? createAnyBoostersRecord(() => 0),
            },
        };
    }

    public static useExtraCardBoosters(state: IGameState): IGameState {
        const targets: string[] = [];
        let cardState: ICardsState = CardStateHelper.addNewCards(
            state.levelState.cardsState,
            state.playerState.inventory[BoosterType.PLUS_ONE],
            targets
        );

        state = {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: cardState,
            },
            playerState: {
                ...state.playerState,
                inventory: {
                    ...state.playerState.inventory,
                    [BoosterType.PLUS_ONE]: 0,
                },
            },
        };
        return state;
    }
    public static checkLevelCompleted(state: IGameState): boolean {
        return ProgressStateHelper.getLevelStars(state, state.levelState.levelNumber, state.levelState.levelType) > 0;
    }

    public static queueFTUXTasksIfNeeded(state: IGameState): IGameState {
        // TODO: Use prio to filter correct tasks
        const FTUXtask = FTUX_LEVEL_TASKS.find(ftux => ftux.condition(state));

        if (FTUXtask) {
            state = LevelStateHelper.setStateTaskData(state, {
                task: FTUXtask.taskType,
            });
        }

        return state;
    }
}

export function isNearEndGame(state: IGameState) {
    return state.levelState.cardsState.inStack.length <= 3 && state.levelState.cardsState.inPlay.length > 0;
}

export const getAllRemainingCardsReward = (props: {
    cards: ICardStateData[];
    levelCost: number;
    startFactor: number;
    linearFactor: number;
    firstComplete: boolean;
}) => {
    let result = 0;

    for (let i = 0; i < props.cards.length; i += 1) {
        result += getSingleRemainingCardReward({
            levelCost: props.levelCost,
            startFactor: props.startFactor,
            linearFactor: props.linearFactor,
            card: props.cards[i],
            cardIndex: i,
            firstComplete: props.firstComplete,
        });
    }

    return result;
};

const multiplierForRemainingCard: { [key in CardType]: ((card: ICardStateData & { type: key }) => number) | null } = {
    [CardType.boom]: null,
    [CardType.zap]: null,
    [CardType.lock]: null,
    [CardType.key]: null,
    [CardType.plus_three]: null,
    [CardType.value]: null,
    [CardType.rock]: null,
    [CardType.scissors]: null,
    [CardType.tornado]: null,
    [CardType.gold]: null,
    [CardType.joker]: null,
    [CardType.joker_pre_booster]: card => (card.madeByBooster ? 0.5 : 0.5),
    [CardType.canister_pre_booster]: () => 1.5,
};

export const getSingleRemainingCardReward = (props: {
    card: ICardStateData;
    cardIndex: number;
    levelCost: number;
    startFactor: number;
    linearFactor: number;
    firstComplete: boolean;
}): number => {
    const { card, cardIndex, levelCost, startFactor, linearFactor } = props;
    const getMultiplier = multiplierForRemainingCard[card.type];
    let result: number;

    if (getMultiplier) {
        // @ts-ignore
        result = levelCost * getMultiplier(card);
    } else if (props.firstComplete) {
        const startReward = levelCost * startFactor;
        const linearReward = levelCost * linearFactor;

        result = Math.ceil(startReward + linearReward * cardIndex);
    } else {
        result = 5 * (cardIndex + 1);
    }

    return result;
};

export const isGoldLevel = (state: Immutable<ILevelState>) => {
    return state.winCriteria.some(data => data.type === WinCriteriaType.CLEAR_GOLD);
};

export const isDynamicAILevel = (state: IGameState, levelType: LevelType = LevelType.Core): boolean => {
    switch (state.levelState.levelType) {
        case LevelType.Core:
            return state.levelState.levelNumber >= LevelHelperSettings.dynamicStartLevel;
        default:
            unexpectedValue(state.levelState.levelType, false);
            return false;
    }
};
export const canShowInterstitialAd = (state: IGameState): boolean => {
     // let the wrapper decide if an ad can be shown
    return true;
    /*return (
        state.levelState.levelNumber >= INTERSTITIAL_START_LEVEL &&
        state.levelState.levelNumber % INTERSTITIAL_FREQUENCY === 0 &&
        !state.playerState.purchases
    );*/
};

export const isLevelComplete = (state: Immutable<ILevelState>): boolean => {
    const remainingGoldCards = state.cardsState.inPlay.filter((cardID: string) => {
        return state.cardsState.cards[cardID].type === CardType.gold;
    });
    return state.cardsState.inPlay.length === 0 || (isGoldLevel(state) && remainingGoldCards.length === 0);
};

export const getHardLevelBGKey = (
    _levelNumber: number,
): string => {
    const levelData: ILevelDataFile = Game.resource.get(`levelData_${_levelNumber}`);
    const episodeIndex = getEpisodeIndex(levelData.settings);
    const fieldID = WorldDataHelper.getFieldData()[episodeIndex].id;
    const { key } = getFieldUnlockShare(fieldID);

    return key;
};
